import axios from "axios";

// WABA Server API Base URL (different from main server)
const WABA_API_BASE = process.env.REACT_APP_WABA_SERVER_URL;

const chatAPI = axios.create({
  baseURL: `${WABA_API_BASE}/api/chat`,
  headers: { "Content-Type": "application/json" },
});

// Add auth token to all requests
chatAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem("userAccessToken");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add user data to headers (required by extractUserFromHeaders middleware)
  if (user.id) {
    config.headers["X-User-Data"] = JSON.stringify({ id: user.id });
  }
  
  return config;
});

/**
 * Get all conversations for the authenticated user
 * @param {string|null} phoneNumberId - Optional phone number ID to filter conversations
 */
export const getConversations = async (phoneNumberId = null) => {
  const params = {};
  if (phoneNumberId) {
    params.phone_number_id = phoneNumberId;
  }
  return chatAPI.get("/conversations", { params });
};

/**
 * Get messages for a specific conversation
 */
export const getMessages = async (customerPhone, phoneNumberId, limit = 50, offset = 0) => {
  if (!customerPhone || !phoneNumberId) {
    throw new Error("customerPhone and phoneNumberId are required");
  }
  return chatAPI.get("/messages", {
    params: {
      customer_phone: String(customerPhone),
      phone_number_id: String(phoneNumberId),
      limit,
      offset,
    },
  });
};

/**
 * Send a text message
 */
export const sendMessage = async (phoneNumberId, customerPhone, messageText) => {
  return chatAPI.post("/send", {
    phone_number_id: phoneNumberId,
    customer_phone: customerPhone,
    message_text: messageText,
  });
};

/**
 * Send a media message
 */
export const sendMediaMessage = async (phoneNumberId, customerPhone, mediaUrl, mediaType, caption = null) => {
  return chatAPI.post("/send", {
    phone_number_id: phoneNumberId,
    customer_phone: customerPhone,
    media_url: mediaUrl,
    media_type: mediaType,
    message_text: caption,
  });
};

/**
 * Check if conversation window is open (24-hour rule)
 */
export const checkWindowStatus = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/window-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Download media from Meta and store in GCS (on-demand)
 */
export const downloadMedia = async (messageId, phoneNumberId) => {
  return chatAPI.post("/download-media", {
    message_id: messageId,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Upload file for chat media messages
 * @param {File} file - File object from input
 * @returns {Promise} - Response with media_url and media_type
 */
export const uploadChatMedia = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        const response = await chatAPI.post("/upload-media", {
          file: base64,
          fileName: file.name,
          contentType: file.type,
        });
        resolve(response);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Mark messages as read for a conversation
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with marked count
 */
export const markMessagesAsRead = async (customerPhone, phoneNumberId) => {
  return chatAPI.post("/mark-read", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
  });
};

/**
 * Get AI status for a specific chat
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with ai_enabled status
 */
export const getChatAiStatus = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/ai-status", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Toggle AI status for a specific chat
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {boolean} aiEnabled - Whether AI should be enabled
 * @returns {Promise} - Response with updated ai_enabled status
 */
export const toggleChatAiStatus = async (customerPhone, phoneNumberId, aiEnabled) => {
  return chatAPI.post("/toggle-ai", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    ai_enabled: aiEnabled,
  });
};

/**
 * Get engaged customers with scores and filters
 * @param {Object} filters - Filter options
 * @param {string} filters.phone_number_id - Optional phone number ID
 * @param {number} filters.min_score - Minimum total score
 * @param {number} filters.max_score - Maximum total score
 * @param {string} filters.start_date - Start date (YYYY-MM-DD)
 * @param {string} filters.end_date - End date (YYYY-MM-DD)
 * @param {string} filters.sort_by - Sort by "total_score" or "avg_score"
 * @param {string} filters.sort_order - Sort order "asc" or "desc"
 * @param {number} filters.limit - Number of results
 * @param {number} filters.offset - Offset for pagination
 * @returns {Promise} - Response with engaged customers
 */
export const getEngagedCustomers = async (filters = {}) => {
  return chatAPI.get("/engaged-customers", { params: filters });
};

/**
 * Export all engaged customers chats with scores
 * @param {Object} filters - Filter options (same as getEngagedCustomers)
 * @returns {Promise} - Response with all engaged customers data
 */
export const exportAllEngagedCustomers = async (filters = {}) => {
  return chatAPI.get("/engaged-customers/export-all", { params: filters });
};

/**
 * Export engaged customer chat with scores
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @returns {Promise} - Response with messages and engagement scores
 */
export const exportEngagedCustomerChat = async (customerPhone, phoneNumberId) => {
  return chatAPI.get("/engaged-customers/export", {
    params: {
      customer_phone: customerPhone,
      phone_number_id: phoneNumberId,
    },
  });
};

/**
 * Generate chat summary using ChatGPT
 * @param {string} customerPhone - Customer phone number
 * @param {string} phoneNumberId - Phone number ID
 * @param {number} messageCount - Number of last messages to include (default: 50)
 * @returns {Promise} - Response with structured summary
 */
export const generateChatSummary = async (customerPhone, phoneNumberId, messageCount = 50) => {
  return chatAPI.post("/generate-summary", {
    customer_phone: customerPhone,
    phone_number_id: phoneNumberId,
    message_count: messageCount,
  });
};

export default chatAPI;

